<template>
  <div class="config" ref="root">
    <div class="config__options">
      <span class="config__title">Start Building 🏗️</span>
      <span class="config__text">
        <template v-if="shouldUseDrag">
          Help us finish this profile by dragging these items to the phone.
        </template>
        <template v-else
          >Help us finish this profile by selecting these items.</template
        >
      </span>
      <transition-group
        class="config__options__inner"
        :class="{ 'config__options__inner--active': optionsAnimationComplete }"
        name="list"
        tag="div"
        ref="optionsContainer"
        :duration="350"
      >
        <Option
          class="config__option"
          v-for="{ type, title, description, isPro } in options"
          :key="title"
          :is-pro="isPro"
          :variant="shouldUseDrag ? '' : 'button'"
          @dragstart="startDrag($event, type)"
          @dragend="isDragging = false"
          @click="onOptionClick(type)"
        >
          {{ title }}
          <template slot="description">{{ description }}</template>
        </Option>
        <div class="config__options__done" key="going" v-if="options.length">
          Keep going 💪
        </div>
        <div class="config__options__done" key="done" v-else>Nice work ✨</div>
      </transition-group>
    </div>
    <div class="config__preview">
      <div class="config__preview__inner">
        <client-only>
          <Preview
            class="config__preview__device"
            :profile="profile"
            :social-accounts="socialAccounts"
            :links="links"
            :theme="theme"
            :default-profile-image="'none'"
            ref="preview"
          >
            <div
              class="config__preview__dropzone"
              :class="{ 'config__preview__dropzone--active': isDragging }"
              slot="afterProfile"
              @drop="onDrop"
              @dragover.prevent
              @dragenter.prevent
              ref="dropzone"
              style="display: flex; opacity: 0"
            >
              <Shape
                class="config__preview__dropzone__btn"
                width="250px"
                height="60px"
              />
              <div class="config__preview__dropzone__text">
                Drag and drop here
              </div>
            </div>
          </Preview>
        </client-only>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Preview,
  Shape,
  createThemeFromSettings,
  useIsVisible,
  defaultDocument,
} from '@campsite-bio/component-lib';
import { computed, ref, watch } from '@nuxtjs/composition-api';
import uniqueId from 'lodash/uniqueId';
import anime from 'animejs';

import {
  PROFILE_DEFAULT,
  FEED_LINK,
  CAROUSEL_LINK,
  OPT_IN_LINK,
  IMAGE_GRID_LINK,
} from '@/constants';
import Option from './option';
import { useMediaQuery } from '@vueuse/core';

const OPTIONS = [
  {
    type: 'link',
    title: 'Link 🔗',
    description: 'Add a link to your bio',
  },
  {
    type: 'title',
    title: 'Title',
    description: 'Organize your links with a title.',
  },
  {
    type: 'social',
    title: 'Social Accounts',
    description: 'Add all of your social accounts.',
  },
  {
    type: 'image-grid',
    title: 'Image Grid',
    description: 'An image grid powered by your Instagram feed.',
  },
  {
    type: 'carousel',
    title: 'Carousel',
    description: 'Get a swipe-able carousel of your images.',
  },
  {
    type: 'embed-video',
    title: 'Video Embed 🎬',
    description: 'Embed a video right in your bio.',
  },
  {
    type: 'embed-music',
    title: 'Music Embed 🎵',
    description: 'Embed music right in your bio.',
  },
  {
    type: 'opt-in',
    title: 'Opt-in Form ⚡',
    description: 'Collect email or sms numbers from your visitors',
    isPro: true,
  },
  {
    type: 'feed',
    title: 'Feed 📡',
    description:
      'Pull in your latest blog posts or Youtube videos automatically.',
    isPro: true,
  },
];

export default {
  components: {
    Preview,
    Shape,
    Option,
  },

  setup() {
    const profile = ref(PROFILE_DEFAULT.profile);
    const socialAccounts = ref([]);
    const links = ref(PROFILE_DEFAULT.links);
    const options = ref(OPTIONS);
    const root = ref(null);
    const optionsContainer = ref(null);
    const preview = ref(null);
    const dropzone = ref(null);
    const optionsAnimationComplete = ref(false);
    const isDragging = ref(false);
    const supportsDrag = ref(
      defaultDocument
        ? 'draggable' in defaultDocument.createElement('span')
        : true,
    );

    const isLargerScreen = useMediaQuery('(min-width: 992px)');

    const theme = computed(() => {
      return createThemeFromSettings(profile.value);
    });

    const shouldUseDrag = computed(() => {
      return supportsDrag.value && isLargerScreen.value;
    });

    const isVisible = useIsVisible(root, {
      runOnce: true,
      threshold: 0.25,
    });

    watch(isVisible, () => animateIn());

    function animateIn() {
      const timeline = anime.timeline({
        easing: 'easeInOutSine',
        duration: 1000,
      });
      const optionEls = optionsContainer.value.$children.map(
        (option) => option.$el,
      );

      timeline
        .add({
          targets: [
            root.value.querySelector('.config__title'),
            root.value.querySelector('.config__text'),
          ],
          opacity: [0, 1],
        })
        .add(
          {
            targets: optionEls,
            translateY: ['100%', 0],
            opacity: [0, 1],
            delay: anime.stagger(100),
            duration: 500,
            complete() {
              optionEls.forEach((el) => {
                el.style.opacity = null;
              });
              optionsAnimationComplete.value = true;
            },
          },
          '-=250',
        );

      if (shouldUseDrag.value) {
        timeline.add(
          {
            targets: dropzone.value,
            opacity: [0, 1, 1, 1, 0],
            duration: 3000,
            complete() {
              dropzone.value.style.display = null;
              dropzone.value.style.opacity = null;
            },
          },
          '-=1000',
        );
      }

      timeline.add(
        {
          targets: preview.value.$el,
          opacity: [0, 1],
          duration: 2000,
        },
        0,
      );
    }

    function add(type) {
      const helper = {
        link: {
          label: 'A link is the building block of your bio.',
          url: 'https://campsite.bio',
        },
        title: {
          label: 'A title helps you organize your content',
          type: 'title',
        },
        divider: {
          label: 'small',
          type: 'delimiter',
        },
        feed: {
          ...FEED_LINK,
        },
        'embed-video': {
          type: 'embed',
          url: 'https://www.youtube.com/watch?v=pXRviuL6vMY',
          embed_options: {
            remove_bg: false,
            embed_type: 'embed',
          },
        },
        'embed-music': {
          type: 'embed',
          url: 'https://open.spotify.com/track/3uwYgNxFDfx1GoLB6tLoUn?si=6450e541874c42a8',
          embed_options: {
            remove_bg: false,
            embed_type: 'embed',
          },
        },
        carousel: {
          ...CAROUSEL_LINK,
        },
        'opt-in': {
          ...OPT_IN_LINK,
        },
        'image-grid': {
          ...IMAGE_GRID_LINK,
        },
      };

      const link = helper[type];

      if (link) {
        links.value.unshift({
          _id: { $oid: uniqueId('link') },
          enabled: true,
          ...link,
        });
      }

      if (type === 'social') {
        socialAccounts.value = PROFILE_DEFAULT.socialAccounts;
      }

      options.value = options.value.filter(
        ({ type: optionType }) => optionType !== type,
      );
    }

    function startDrag(e, type) {
      isDragging.value = true;
      e.dataTransfer.dropEffect = 'move';
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('optionType', type);
    }

    function onDrop(e) {
      const type = e.dataTransfer.getData('optionType');

      setTimeout(() => {
        add(type);
      }, 100);
    }

    function onOptionClick(type) {
      if (!shouldUseDrag.value) add(type);
    }

    return {
      root,
      profile,
      socialAccounts,
      links,
      theme,
      add,
      options,
      optionsContainer,
      preview,
      dropzone,
      optionsAnimationComplete,
      isDragging,
      startDrag,
      onDrop,
      shouldUseDrag,
      onOptionClick,
    };
  },
};
</script>

<style lang="scss" scoped>
.config {
  display: grid;
  justify-content: center;
  text-align: center;
  gap: 1rem;

  @media (min-width: 552px) {
    grid-template-columns: 1fr 300px;
    gap: 2rem;
  }

  &__title {
    display: block;
    border-radius: var(--g-border-radius-standard);
    border-radius: Min(
      var(--g-border-radius-standard),
      var(--g-border-radius-min)
    );
    font-size: 1.5rem;
    font-weight: 700;
    opacity: 0;
  }

  &__text {
    color: var(--g-color-gray-600);
    margin-top: 0.25rem;
    font-size: 0.85rem;
    display: block;
    opacity: 0;
  }

  &__options {
    position: relative;
    overflow: hidden;

    @media (min-width: 552px) {
      overflow: unset;
    }

    &__inner {
      display: flex;
      padding-top: 1rem;

      @media (min-width: 552px) {
        display: block;
        position: absolute;
        width: 100%;
        min-height: calc(100% - 50px);

        &:after {
          background: linear-gradient(1800deg, #fff 10%, transparent 100%);
          content: '';
          bottom: 0;
          height: 50%;
          position: absolute;
          left: 0;
          width: 110%;
          opacity: 0;
          transition: opacity var(--g-transition-speed);
        }
      }

      &--active {
        &:after {
          opacity: 1;
        }

        .config__option {
          opacity: 1;

          &:nth-child(n + 2) {
            pointer-events: none;
            opacity: 0.5;

            @media (min-width: 552px) {
              opacity: 0.8;
            }
          }

          &:nth-child(n + 3) {
            opacity: 0.5;
          }
        }

        .config__options__done {
          opacity: 1;
        }
      }
    }

    &__done {
      font-weight: 700;
      font-size: 1.25rem;
      margin-bottom: 1rem;
      opacity: 0;
      text-align: center;
    }
  }

  &__option {
    opacity: 0;
    width: 40%;
    flex-shrink: 0;
    margin-right: 0.5rem;

    &::v-deep .option__btn {
      height: 100%;
    }

    @media (min-width: 552px) {
      margin-bottom: 0.5rem;
      margin-right: 0;
      width: unset;
    }
  }

  &__preview {
    justify-self: center;
    position: relative;
    width: 300px;

    &__inner {
      padding-top: 202.803738317757%;
    }

    &__device {
      position: absolute;
      opacity: 0;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;

      &::v-deep .site__delimiter {
        height: 3px;
      }
    }

    &__dropzone {
      background: rgba(255, 255, 255, 0.85);
      border: 3px dashed var(--g-color-gray-600);
      border-radius: 40px;
      display: none;
      align-items: center;
      justify-content: center;
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      flex-direction: column;

      &--active {
        display: flex;
      }

      &__btn {
        &::v-deep .v-shape {
          background: #fff;
          border: 2px dashed var(--g-color-primary);
        }
      }

      &__text {
        font-size: 1.5em;
        font-weight: 700;
        margin-top: 1rem;
      }
    }
  }
}

.list-enter.list-enter,
.list-leave-to.list-leave-to {
  opacity: 0;
  transform: translateY(50%) !important;
  transition: all 350ms;

  @media (min-width: 552px) {
    transform: translateX(50%) !important;
  }
}
</style>
